import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';

/**
 * Sign-in page. Magic link only. Users must have paid via /get-started first.
 * If the email has no account, we show a friendly CTA to enroll. Google OAuth
 * has been removed for launch: every account is tied to a paid Stripe purchase.
 */
export default function LoginPage({ onAuth: _onAuth }: { onAuth: () => Promise<void> }) {
  const [params] = useSearchParams();
  const next = params.get('next') || '/dashboard';

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const errorCode = params.get('error');
  const initialError =
    errorCode === 'google_disabled'
      ? 'Google sign in has been turned off. Use the email link below to sign in.'
      : errorCode === 'oauth_state_mismatch' ||
        errorCode === 'oauth_failed' ||
        errorCode === 'oauth_unknown'
      ? 'Sign in failed. Try emailing yourself a link.'
      : null;
  const [error, setError] = useState<string | null>(initialError);
  const [notEnrolled, setNotEnrolled] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);

  async function sendMagic(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotEnrolled(false);
    setLoading(true);
    try {
      const res = await api.post<{ ok: true; devLink?: string }>('/api/auth/magic-link', {
        email,
        next,
      });
      setSent(true);
      if (res.devLink) setDevLink(res.devLink);
    } catch (err) {
      const code = (err as { error?: string })?.error;
      if (code === 'invalid_email') setError('Enter a valid email.');
      else if (code === 'not_enrolled') setNotEnrolled(true);
      else if (code === 'account_blocked') {
        setError(
          'This account has been paused or refunded. Contact support at marshall@constare.academy.'
        );
      } else setError('Could not send the link. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-md py-12">
      <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
      <p className="mt-2 text-sm text-muted">
        Enter the email you paid with. We'll send you a sign-in link.
      </p>

      {!sent && !notEnrolled ? (
        <form onSubmit={sendMagic} className="mt-6 space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-muted">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-line bg-elevated px-3 py-2.5 text-text placeholder:text-muted focus:border-amber focus:outline-none"
              placeholder="you@domain.com"
              data-testid="input-login-email"
            />
          </label>
          {error && <div className="text-sm text-red-400">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-amber px-4 py-2.5 font-semibold text-ink hover:bg-amber-hover disabled:opacity-60"
            data-testid="button-send-magic-link"
          >
            {loading ? 'Sending…' : 'Email me a sign in link'}
          </button>
          <p className="text-center text-xs text-muted">
            No account yet?{' '}
            <Link to="/" className="text-amber hover:underline">
              Enroll here
            </Link>
            .
          </p>
        </form>
      ) : notEnrolled ? (
        <div
          className="mt-6 rounded-md border border-amber/40 bg-amber/5 p-5 text-sm"
          data-testid="notice-not-enrolled"
        >
          <p className="text-text font-medium">This email is not enrolled.</p>
          <p className="mt-2 text-muted">
            We do not see a paid enrollment for <span className="font-mono">{email}</span>.
            To join Constare Academy, enroll first.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/"
              className="inline-block rounded-md bg-amber px-4 py-2 text-sm font-semibold text-ink hover:bg-amber-hover"
              data-testid="link-enroll-from-login"
            >
              Enroll now
            </Link>
            <button
              onClick={() => {
                setNotEnrolled(false);
                setEmail('');
              }}
              className="text-sm text-muted hover:text-text underline"
              data-testid="button-try-different-email"
            >
              Try a different email
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-6 rounded-md border border-line bg-surface p-5 text-sm">
          <p className="text-text">Check your email.</p>
          <p className="mt-1 text-muted">
            We sent a sign in link to <span className="font-mono">{email}</span>. The link expires in 15 minutes.
          </p>
          {devLink && (
            <div className="mt-4 rounded border border-dashed border-amber/40 bg-elevated p-3 text-xs">
              <div className="text-amber">DEV ONLY (no Resend key set)</div>
              <a href={devLink} className="break-all text-text hover:underline">
                {devLink}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
